import React, { useEffect, useMemo, useRef, useState } from "react";
import MarginAdvancedLimitOrderTypeModal, {
  advancedLimitOrderTypeLabel,
} from "./MarginAdvancedLimitOrderTypeModal";

/**
 * MARGIN — Advanced conditional order forms (NOT wired to `/spot/v1/orders` yet).
 * =============================================================================
 * Parent owns each slice of state (`marginForm*` in `margin/index.js`). This file
 * only renders inputs so each tab’s payload stays obvious in one place.
 *
 * Conditional, TP/SL, TWAP, Iceberg Pro, Loop, Trailing Stop & Advanced Limit: reuse the same “filled” field shell as the Limit tab (`trade_price_field`,
 * `trade_amount_field_wrap`, `trade_pct_slider_*`, `stop_avail_*`) — see TradeCenterSection.
 */

/** Nudge advanced-limit price by `tick` (aligns with main limit stepper logic). */
function nudgeAdvLimitPriceStr(raw, direction, tick, prec) {
  const t = Number(tick) > 0 && Number.isFinite(Number(tick)) ? Number(tick) : 0.01;
  const p = Number.isFinite(prec) && prec >= 0 ? Math.min(Math.floor(prec), 16) : 8;
  const s = String(raw ?? "").replace(/,/g, "").trim();
  const n = parseFloat(s);
  if (!Number.isFinite(n) || n <= 0) {
    if (direction > 0) return parseFloat(t.toFixed(p)).toString();
    return "";
  }
  const next = Math.round((n + direction * t) / t) * t;
  const clamped = Math.max(next, t);
  return parseFloat(clamped.toFixed(p)).toString();
}

/** Strip commas; return numeric string suitable for state, or "" if invalid. */
function normalizePriceSeed(raw) {
  if (raw === "" || raw == null) return "";
  const s = String(raw).replace(/,/g, "");
  const n = Number(s);
  return Number.isFinite(n) && n > 0 ? s : "";
}

/** Limit-for-TP / Limit-for-SL row: same shell as Limit tab price (value + quote suffix) + one toggle (Market ⟷ Limit). */
function TpSlLimitMarketRow({ label, isMarket, onToggle, limitPrice, onLimitPriceChange, quoteSymbol, formatPriceThousands }) {
  const [focused, setFocused] = useState(false);
  const fmt = typeof formatPriceThousands === "function" ? formatPriceThousands : (s) => s;
  const displayPrice =
    !focused && limitPrice && String(limitPrice).trim() !== "" ? fmt(String(limitPrice)) : limitPrice;

  return (
    <div className="form-group mb-3 trade_price_field_wrap">
      <label>{label}</label>
      <div className="d-flex gap-2 align-items-stretch agce-margin-tp-sl-price-row">
        {isMarket ? (
          <div className="trade_price_field is-readonly flex-grow-1" style={{ minWidth: 0 }}>
            <input
              type="text"
              className="trade_price_input"
              readOnly
              value=""
              placeholder="Best Market Price"
              aria-label={label}
            />
          </div>
        ) : (
          <div className="trade_price_field flex-grow-1" style={{ minWidth: 0 }}>
            <input
              type="text"
              className="trade_price_input"
              inputMode="decimal"
              autoComplete="off"
              placeholder=""
              value={displayPrice}
              onFocus={() => setFocused(true)}
              onBlur={() => setFocused(false)}
              onChange={(e) => onLimitPriceChange(e.target.value.replace(/,/g, ""))}
              aria-label={label}
            />
            <span className="trade_price_suffix">{quoteSymbol}</span>
          </div>
        )}
        <button type="button" className="agce-margin-tp-sl-toggle-btn" onClick={onToggle} aria-pressed={!isMarket}>
          {isMarket ? "Market" : "Limit"}
        </button>
      </div>
    </div>
  );
}

/** TP/SL — Limit tab–style controls (marginFormTpSl in margin/index.js). */
function MarginTpSlFields({
  marginSurfaceTab,
  value,
  set,
  baseSymbol,
  quoteSymbol,
  side,
  token,
  buyCoinBal,
  sellCoinBal,
  formatTotal,
  formatPriceThousands,
  marketPriceSeed,
  onPlusClick,
}) {
  const q = quoteSymbol || "USDT";
  const b = baseSymbol || "BTC";
  const seedStr = normalizePriceSeed(marketPriceSeed);
  const [tpTrigFocus, setTpTrigFocus] = useState(false);
  const [slTrigFocus, setSlTrigFocus] = useState(false);
  const fmt = typeof formatPriceThousands === "function" ? formatPriceThousands : (x) => x;
  const tpTrigDisplay =
    !tpTrigFocus && value.tpTriggerPrice && String(value.tpTriggerPrice).trim() !== ""
      ? fmt(String(value.tpTriggerPrice).replace(/,/g, ""))
      : value.tpTriggerPrice;
  const slTrigDisplay =
    !slTrigFocus && value.slTriggerPrice && String(value.slTriggerPrice).trim() !== ""
      ? fmt(String(value.slTriggerPrice).replace(/,/g, ""))
      : value.slTriggerPrice;
  const isBuy = side === "buy";
  const quoteBal = buyCoinBal != null ? parseFloat(Number(buyCoinBal).toFixed(8)) : 0;
  const baseBal = sellCoinBal != null ? parseFloat(Number(sellCoinBal).toFixed(8)) : 0;
  const availLine = isBuy ? `${token ? quoteBal : "0"} ${q}` : `${token ? baseBal : "0"} ${b}`;
  const maxLine = isBuy ? (token && buyCoinBal != null ? `${formatTotal?.(buyCoinBal) ?? "0"} ${q}` : `0 ${q}`) : (token && sellCoinBal != null ? `${formatTotal?.(sellCoinBal) ?? "0"} ${b}` : `0 ${b}`);

  const setPct = (pct) => {
    const safe = [0, 25, 50, 75, 100].includes(pct) ? pct : 0;
    set((p) => ({ ...p, amountPercent: safe }));
  };

  const dotted = { textDecoration: "underline", textDecorationStyle: "dotted" };

  return (
    <div className="margin-advanced-block margin-advanced-block--tp-sl" data-margin-form="TP_SL">
      <div className="form-group mb-3 trade_amount_field_wrap">
        <label>TP Trigger Price(≤)</label>
        <div className="input-group trade_amount_field_limit">
          <input
            type="text"
            className="form-control"
            inputMode="decimal"
            autoComplete="off"
            placeholder=""
            value={tpTrigDisplay}
            onFocus={() => setTpTrigFocus(true)}
            onBlur={() => setTpTrigFocus(false)}
            onChange={(e) =>
              set((p) => ({ ...p, tpTriggerPrice: e.target.value.replace(/,/g, "") }))
            }
            aria-label="Take profit trigger price"
          />
          <span className="input-group-text text-start trade_amount_coin_badge">
            <small>{q}</small>
          </span>
        </div>
      </div>

      <TpSlLimitMarketRow
        label="Limit for TP"
        isMarket={value.tpLimitMarket}
        onToggle={() =>
          set((p) =>
            p.tpLimitMarket
              ? { ...p, tpLimitMarket: false, tpLimitPrice: p.tpLimitPrice || seedStr }
              : { ...p, tpLimitMarket: true, tpLimitPrice: "" },
          )
        }
        limitPrice={value.tpLimitPrice}
        onLimitPriceChange={(v) => set((p) => ({ ...p, tpLimitPrice: v }))}
        quoteSymbol={q}
        formatPriceThousands={formatPriceThousands}
      />

      <div className="form-group mb-3 trade_amount_field_wrap">
        <label>SL Trigger Price(≥)</label>
        <div className="input-group trade_amount_field_limit">
          <input
            type="text"
            className="form-control"
            inputMode="decimal"
            autoComplete="off"
            value={slTrigDisplay}
            onFocus={() => setSlTrigFocus(true)}
            onBlur={() => setSlTrigFocus(false)}
            onChange={(e) =>
              set((p) => ({ ...p, slTriggerPrice: e.target.value.replace(/,/g, "") }))
            }
            aria-label="Stop loss trigger price"
          />
          <span className="input-group-text text-start trade_amount_coin_badge">
            <small>{q}</small>
          </span>
        </div>
      </div>

      <TpSlLimitMarketRow
        label="Limit for SL"
        isMarket={value.slLimitMarket}
        onToggle={() =>
          set((p) =>
            p.slLimitMarket
              ? { ...p, slLimitMarket: false, slLimitPrice: p.slLimitPrice || seedStr }
              : { ...p, slLimitMarket: true, slLimitPrice: "" },
          )
        }
        limitPrice={value.slLimitPrice}
        onLimitPriceChange={(v) => set((p) => ({ ...p, slLimitPrice: v }))}
        quoteSymbol={q}
        formatPriceThousands={formatPriceThousands}
      />

      <div className="form-group mb-2 trade_amount_field_wrap">
        <label>Amount</label>
        <div className="input-group trade_amount_field_limit">
          <input
            type="text"
            className="form-control"
            placeholder="Amount"
            value={value.quantity}
            onChange={(e) => set((p) => ({ ...p, quantity: e.target.value }))}
            aria-label="Amount"
          />
          <span className="input-group-text text-start trade_amount_coin_badge">
            <small>{b}</small>
          </span>
        </div>
      </div>

      <div className="limit_buy_extras">
        <div className="stop_slider_row limit_buy_slider_row trade_pct_slider_row">
          <div className="trade_pct_slider_shell" style={{ "--fill-pct": `${value.amountPercent}%` }}>
            <div className="trade_pct_track_line" aria-hidden />
            <input
              className="trade_pct_slider_input"
              type="range"
              min={0}
              max={100}
              step={25}
              value={value.amountPercent}
              onChange={(e) => setPct(Number(e.target.value))}
              aria-label="Amount percentage"
            />
            <div className="trade_pct_marks" aria-hidden>
              {[0, 1, 2, 3, 4].map((step) => (
                <span
                  key={step}
                  className={`trade_pct_dot ${step === 0 ? "trade_pct_dot--anchor" : ""} ${step * 25 <= value.amountPercent ? "trade_pct_dot--fill" : ""}`}
                />
              ))}
            </div>
            <div className="trade_pct_labels" aria-hidden>
              <span>0%</span>
              <span>25%</span>
              <span>50%</span>
              <span>75%</span>
              <span>100%</span>
            </div>
          </div>
        </div>
      </div>

      <div className="stop_avail_block limit_buy_avail mt-2">
        <div className="stop_avail_row limit_buy_avail_row">
          <span className="stop_avail_label">Available</span>
          <div className="stop_avail_rgt">
            <span className="stop_avail_val">{availLine}</span>
            <button type="button" className="limit_buy_plus" onClick={onPlusClick} aria-label="Add Funds">
              <img src="/images/plushicon.svg" alt="plus" />
            </button>
          </div>
        </div>
        {marginSurfaceTab !== "spot" && (
          <>
            <div className="stop_avail_row limit_buy_avail_row">
              <span className="stop_avail_label" style={dotted}>
                Max
              </span>
              <span className="stop_avail_val">{maxLine}</span>
            </div>
            <div className="stop_avail_row limit_buy_avail_row">
              <span className="stop_avail_label" style={dotted}>
                Borrowing
              </span>
              <span className="stop_avail_val">0 {q}</span>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

const twapDotted = { textDecoration: "underline", textDecorationStyle: "dotted" };

const TWAP_PRICE_VAR_OPTIONS = ["0.01", "0.02", "0.05", "0.08", "0.1", "0.15", "0.2"];

const TWAP_TIME_INTERVAL_UNIT_OPTIONS = [
  { label: "Hour", value: "Hour" },
  { label: "Minute", value: "Minute" },
];

const ICEBERG_PV_UNIT_OPTIONS = [
  { label: "%", value: "%" },
  { label: "USDT", value: "USDT" },
];

const LOOP_PRICE_MODE_OPTIONS = [
  { label: "Custom", value: "CUSTOM" },
  { label: "Last Price", value: "LAST_PRICE" },
  { label: "Buy 1", value: "BUY_1" },
  { label: "Buy 2", value: "BUY_2" },
  { label: "Buy 3", value: "BUY_3" },
];

const LOOP_VALID_FOR_OPTIONS = ["1Day", "2Days", "3Days", "4Days", "5Days"].map((v) => ({ label: v, value: v }));

const CONDITIONAL_VALID_FOR_OPTIONS = [
  { label: "Permanent", value: "Permanent" },
  ...LOOP_VALID_FOR_OPTIONS,
];

function TwapPickMenu({ options, onPick }) {
  return (
    <ul className="agce-margin-twap-pick__menu" role="menu">
      {options.map((o) => (
        <li key={o.value} className="agce-margin-twap-pick__menu-item" role="none">
          <button type="button" className="agce-margin-twap-pick__option" role="menuitem" onClick={() => onPick(o.value)}>
            {o.label}
          </button>
        </li>
      ))}
    </ul>
  );
}

/** Loop Order / Valid for — menu rows with checkmark on selected value */
function LoopPickMenu({ options, selectedValue, onPick }) {
  return (
    <ul className="agce-margin-loop-pick__menu" role="menu">
      {options.map((o) => (
        <li key={o.value} className="agce-margin-loop-pick__menu-item" role="none">
          <button type="button" className="agce-margin-loop-pick__option" role="menuitem" onClick={() => onPick(o.value)}>
            <span>{o.label}</span>
            {selectedValue === o.value ? (
              <i className="ri-checkbox-circle-fill agce-margin-loop-pick__check" aria-hidden />
            ) : (
              <span className="agce-margin-loop-pick__check-slot" aria-hidden />
            )}
          </button>
        </li>
      ))}
    </ul>
  );
}

/** TWAP — Limit tab–style filled rows + collapsible Advanced Options (`marginFormTwap` in margin/index.js). */
function MarginTwapFields({
  marginSurfaceTab,
  value,
  set,
  baseSymbol,
  quoteSymbol,
  side,
  token,
  buyCoinBal,
  sellCoinBal,
  formatTotal,
  formatPriceThousands,
  onPlusClick,
}) {
  const q = quoteSymbol || "USDT";
  const b = baseSymbol || "BTC";
  const isBuy = side === "buy";
  const quoteBal = buyCoinBal != null ? parseFloat(Number(buyCoinBal).toFixed(8)) : 0;
  const baseBal = sellCoinBal != null ? parseFloat(Number(sellCoinBal).toFixed(8)) : 0;
  const availLine = isBuy ? `${token ? quoteBal : "0"} ${q}` : `${token ? baseBal : "0"} ${b}`;
  const maxLine = isBuy
    ? token && buyCoinBal != null
      ? `${formatTotal?.(buyCoinBal) ?? "0"} ${q}`
      : `0 ${q}`
    : token && sellCoinBal != null
      ? `${formatTotal?.(sellCoinBal) ?? "0"} ${b}`
      : `0 ${b}`;

  const [advOpen, setAdvOpen] = useState(false);
  const [actFocus, setActFocus] = useState(false);
  const fmt = typeof formatPriceThousands === "function" ? formatPriceThousands : (x) => x;
  const actDisplay =
    !actFocus && value.activationPrice && String(value.activationPrice).trim() !== ""
      ? fmt(String(value.activationPrice).replace(/,/g, ""))
      : value.activationPrice;

  const setPct = (pct) => {
    const safe = [0, 25, 50, 75, 100].includes(pct) ? pct : 0;
    set((p) => ({ ...p, totalPercent: safe }));
  };

  const applyPresetHours = (h) => {
    set((p) => ({ ...p, durationHours: String(h) }));
  };

  const estFilled =
    value.perSubOrderQty && String(value.perSubOrderQty).trim() !== ""
      ? `${fmt(String(value.perSubOrderQty).replace(/,/g, ""))} ${b}`
      : `-- ${b}`;

  const advancedPanelRef = useRef(null);
  /** Which TWAP value-picker menu is open: price var | time unit | per sub */
  const [pickOpen, setPickOpen] = useState(null);

  useEffect(() => {
    if (!advOpen) setPickOpen(null);
  }, [advOpen]);

  useEffect(() => {
    if (!pickOpen) return;
    const onDoc = (e) => {
      if (advancedPanelRef.current && !advancedPanelRef.current.contains(e.target)) {
        setPickOpen(null);
      }
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [pickOpen]);

  const perSubOptions = useMemo(() => {
    const fmtLocal = typeof formatPriceThousands === "function" ? formatPriceThousands : (x) => x;
    const raw = String(value.totalQty || "").replace(/,/g, "");
    const t = parseFloat(raw);
    if (Number.isFinite(t) && t > 0) {
      const q10 = parseFloat((t * 0.1).toFixed(8));
      const q25 = parseFloat((t * 0.25).toFixed(8));
      const q50 = parseFloat((t * 0.5).toFixed(8));
      const q100 = parseFloat(t.toFixed(8));
      return [
        { label: `10% of total (${fmtLocal(String(q10))} ${b})`, value: String(q10) },
        { label: `25% of total (${fmtLocal(String(q25))} ${b})`, value: String(q25) },
        { label: `50% of total (${fmtLocal(String(q50))} ${b})`, value: String(q50) },
        { label: `100% of total (${fmtLocal(String(q100))} ${b})`, value: String(q100) },
      ];
    }
    return [
      { label: `0.0001 ${b}`, value: "0.0001" },
      { label: `0.0005 ${b}`, value: "0.0005" },
      { label: `0.001 ${b}`, value: "0.001" },
      { label: `0.01 ${b}`, value: "0.01" },
    ];
  }, [value.totalQty, b, formatPriceThousands]);

  const priceVarMenuOptions = useMemo(
    () => TWAP_PRICE_VAR_OPTIONS.map((v) => ({ label: `${v}%`, value: v })),
    [],
  );

  return (
    <div className="margin-advanced-block margin-advanced-block--twap" data-margin-form="TWAP">
      <div className="form-group mb-2 trade_amount_field_wrap">
        <label>Total</label>
        <div className="input-group trade_amount_field_limit">
          <input
            type="text"
            className="form-control"
            inputMode="decimal"
            autoComplete="off"
            placeholder=""
            value={value.totalQty}
            onChange={(e) => set((p) => ({ ...p, totalQty: e.target.value.replace(/,/g, "") }))}
            aria-label="TWAP total amount"
          />
          <span className="input-group-text text-start trade_amount_coin_badge">
            <small>{b}</small>
          </span>
        </div>
      </div>

      <div className="limit_buy_extras">
        <div className="stop_slider_row limit_buy_slider_row trade_pct_slider_row">
          <div className="trade_pct_slider_shell" style={{ "--fill-pct": `${value.totalPercent}%` }}>
            <div className="trade_pct_track_line" aria-hidden />
            <input
              className="trade_pct_slider_input"
              type="range"
              min={0}
              max={100}
              step={25}
              value={value.totalPercent}
              onChange={(e) => setPct(Number(e.target.value))}
              aria-label="TWAP total percentage"
            />
            <div className="trade_pct_marks" aria-hidden>
              {[0, 1, 2, 3, 4].map((step) => (
                <span
                  key={step}
                  className={`trade_pct_dot ${step === 0 ? "trade_pct_dot--anchor" : ""} ${step * 25 <= value.totalPercent ? "trade_pct_dot--fill" : ""}`}
                />
              ))}
            </div>
            <div className="trade_pct_labels" aria-hidden>
              <span>0%</span>
              <span>25%</span>
              <span>50%</span>
              <span>75%</span>
              <span>100%</span>
            </div>
          </div>
        </div>
      </div>

      <div className="form-group mb-2 trade_price_field_wrap mt-2">
        <label>Duration</label>
        <div className="agce-margin-twap-duration-shell">
          <div className="agce-margin-twap-duration-cell">
            <input
              type="text"
              className="agce-margin-twap-duration-input"
              inputMode="numeric"
              autoComplete="off"
              placeholder="1-24"
              value={value.durationHours}
              onChange={(e) => set((p) => ({ ...p, durationHours: e.target.value.replace(/[^\d.]/g, "") }))}
              aria-label="Duration hours"
            />
            <span className="agce-margin-twap-duration-suffix">H</span>
          </div>
          <div className="agce-margin-twap-duration-divider" aria-hidden />
          <div className="agce-margin-twap-duration-cell">
            <input
              type="text"
              className="agce-margin-twap-duration-input"
              inputMode="numeric"
              autoComplete="off"
              placeholder="1-60"
              value={value.durationMinutes}
              onChange={(e) => set((p) => ({ ...p, durationMinutes: e.target.value.replace(/[^\d.]/g, "") }))}
              aria-label="Duration minutes"
            />
            <span className="agce-margin-twap-duration-suffix">M</span>
          </div>
        </div>
      </div>

      <div className="agce-margin-twap-preset-row mb-3">
        {[
          { h: 1, label: "1H" },
          { h: 6, label: "6H" },
          { h: 12, label: "12H" },
          { h: 24, label: "24H" },
        ].map(({ h, label }) => (
          <button
            key={h}
            type="button"
            className="agce-margin-twap-preset"
            onClick={() => applyPresetHours(h)}
          >
            {label}
          </button>
        ))}
      </div>

      <button
        type="button"
        className="agce-margin-twap-advanced-toggle"
        onClick={() => setAdvOpen((o) => !o)}
        aria-expanded={advOpen}
      >
        <span className="agce-margin-twap-advanced-toggle__label">Advanced Options</span>
        <i className={`agce-margin-twap-advanced-toggle__icon ri-arrow-${advOpen ? "up" : "down"}-s-line`} aria-hidden />
      </button>

      {advOpen ? (
        <div ref={advancedPanelRef} className="agce-margin-twap-advanced-panel mt-2">
          <div className="form-group mb-3 trade_price_field_wrap">
            <label style={twapDotted}>Price Var.</label>
            <div className={`trade_price_field agce-margin-twap-pick-field${pickOpen === "pv" ? " is-open" : ""}`}>
              <button
                type="button"
                className={`agce-margin-twap-pick-rowbtn${!value.priceVariance ? " agce-margin-twap-pick-rowbtn--muted" : ""}`}
                onClick={() => setPickOpen((k) => (k === "pv" ? null : "pv"))}
                aria-expanded={pickOpen === "pv"}
                aria-haspopup="menu"
              >
                <span className="agce-margin-twap-pick-rowbtn__text">
                  {value.priceVariance || "0.01–0.2"}
                </span>
                <span className="trade_price_suffix agce-margin-twap-pick-rowbtn__unit">%</span>
                <i
                  className={`agce-margin-twap-pick__chev ri-arrow-${pickOpen === "pv" ? "up" : "down"}-s-line`}
                  aria-hidden
                />
              </button>
              {pickOpen === "pv" ? (
                <TwapPickMenu
                  options={priceVarMenuOptions}
                  onPick={(v) => {
                    set((p) => ({ ...p, priceVariance: v, priceVarianceUnit: "%" }));
                    setPickOpen(null);
                  }}
                />
              ) : null}
            </div>
          </div>

          <div className="form-group mb-3 trade_price_field_wrap">
            <label style={twapDotted}>Activation Price</label>
            <div className="trade_price_field">
              <input
                type="text"
                className="trade_price_input"
                inputMode="decimal"
                autoComplete="off"
                placeholder="≤ Market Price"
                value={actDisplay}
                onFocus={() => setActFocus(true)}
                onBlur={() => setActFocus(false)}
                onChange={(e) =>
                  set((p) => ({ ...p, activationPrice: e.target.value.replace(/,/g, "") }))
                }
                aria-label="Activation price"
              />
              <span className="trade_price_suffix">{q}</span>
            </div>
          </div>

          <div className="form-group mb-3 trade_price_field_wrap">
            <label style={twapDotted}>Time Interval</label>
            <div
              className={`trade_price_field agce-margin-twap-interval-row${pickOpen === "tu" ? " is-open" : ""
                }`}
            >
              <input
                type="text"
                className="trade_price_input"
                inputMode="numeric"
                autoComplete="off"
                placeholder={value.timeIntervalUnit === "Hour" ? "1-12" : "1-60"}
                value={value.timeInterval}
                onChange={(e) =>
                  set((p) => ({ ...p, timeInterval: e.target.value.replace(/[^\d.]/g, "") }))
                }
                aria-label="Time interval value"
              />
              <div className="agce-margin-twap-interval-unit-wrap">
                <button
                  type="button"
                  className="agce-margin-twap-interval-unit-btn"
                  onClick={() => setPickOpen((k) => (k === "tu" ? null : "tu"))}
                  aria-expanded={pickOpen === "tu"}
                  aria-haspopup="menu"
                >
                  <span>{value.timeIntervalUnit}</span>
                  <i
                    className={`agce-margin-twap-pick__chev ri-arrow-${pickOpen === "tu" ? "up" : "down"}-s-line`}
                    aria-hidden
                  />
                </button>
                {pickOpen === "tu" ? (
                  <TwapPickMenu
                    options={TWAP_TIME_INTERVAL_UNIT_OPTIONS}
                    onPick={(v) => {
                      set((p) => ({ ...p, timeIntervalUnit: v }));
                      setPickOpen(null);
                    }}
                  />
                ) : null}
              </div>
            </div>
          </div>

          <div className="form-group mb-3 trade_amount_field_wrap">
            <label style={twapDotted}>Per Sub-Order</label>
            <div className={`agce-margin-twap-per-pick${pickOpen === "per" ? " is-open" : ""}`}>
              <div className="input-group trade_amount_field_limit">
                <button
                  type="button"
                  className={`form-control text-start border-0 shadow-none bg-transparent agce-margin-twap-pick-rowbtn agce-margin-twap-pick-rowbtn--per${!value.perSubOrderQty ? " agce-margin-twap-pick-rowbtn--muted" : ""
                    }`}
                  onClick={() => setPickOpen((k) => (k === "per" ? null : "per"))}
                  aria-expanded={pickOpen === "per"}
                  aria-haspopup="menu"
                >
                  <span className="agce-margin-twap-pick-rowbtn__text">
                    {value.perSubOrderQty
                      ? fmt(String(value.perSubOrderQty).replace(/,/g, ""))
                      : "< Total"}
                  </span>
                  <i
                    className={`agce-margin-twap-pick__chev ri-arrow-${pickOpen === "per" ? "up" : "down"}-s-line`}
                    aria-hidden
                  />
                </button>
                <span className="input-group-text text-start trade_amount_coin_badge">
                  <small>{b}</small>
                </span>
              </div>
              {pickOpen === "per" ? (
                <TwapPickMenu
                  options={perSubOptions}
                  onPick={(v) => {
                    set((p) => ({ ...p, perSubOrderQty: v }));
                    setPickOpen(null);
                  }}
                />
              ) : null}
            </div>
          </div>

          <div className="agce-margin-twap-est-row mb-3">
            <span className="agce-margin-twap-est-label">Est. Filled Per Sub-Order:</span>
            <span className="agce-margin-twap-est-val">{estFilled}</span>
          </div>
        </div>
      ) : null}

      <div className="stop_avail_block limit_buy_avail mt-2">
        <div className="stop_avail_row limit_buy_avail_row">
          <span className="stop_avail_label">Available</span>
          <div className="stop_avail_rgt">
            <span className="stop_avail_val">{availLine}</span>
            <button type="button" className="limit_buy_plus" onClick={onPlusClick} aria-label="Add Funds">
              <img src="/images/plushicon.svg" alt="plus" />
            </button>
          </div>
        </div>
        {marginSurfaceTab !== "spot" && (
          <>
            <div className="stop_avail_row limit_buy_avail_row">
              <span className="stop_avail_label" style={twapDotted}>
                Max
              </span>
              <span className="stop_avail_val">{maxLine}</span>
            </div>
            <div className="stop_avail_row limit_buy_avail_row">
              <span className="stop_avail_label" style={twapDotted}>
                Borrowing
              </span>
              <span className="stop_avail_val">0 {q}</span>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

/** Iceberg Pro — Limit tab–style filled form (`marginFormIcebergPro` in margin/index.js). */
function MarginIcebergProFields({
  marginSurfaceTab,
  value,
  set,
  baseSymbol,
  quoteSymbol,
  side,
  token,
  buyCoinBal,
  sellCoinBal,
  formatTotal,
  formatPriceThousands,
  onPlusClick,
}) {
  const q = quoteSymbol || "USDT";
  const b = baseSymbol || "BTC";
  const isBuy = side === "buy";
  const quoteBal = buyCoinBal != null ? parseFloat(Number(buyCoinBal).toFixed(8)) : 0;
  const baseBal = sellCoinBal != null ? parseFloat(Number(sellCoinBal).toFixed(8)) : 0;
  const availLine = isBuy ? `${token ? quoteBal : "0"} ${q}` : `${token ? baseBal : "0"} ${b}`;
  const maxLine = isBuy
    ? token && buyCoinBal != null
      ? `${formatTotal?.(buyCoinBal) ?? "0"} ${q}`
      : `0 ${q}`
    : token && sellCoinBal != null
      ? `${formatTotal?.(sellCoinBal) ?? "0"} ${b}`
      : `0 ${b}`;

  const [pickOpen, setPickOpen] = useState(null);
  const icebergPanelRef = useRef(null);
  const [actFocus, setActFocus] = useState(false);
  const fmt = typeof formatPriceThousands === "function" ? formatPriceThousands : (x) => x;
  const actDisplay =
    !actFocus && value.activationPrice && String(value.activationPrice).trim() !== ""
      ? fmt(String(value.activationPrice).replace(/,/g, ""))
      : value.activationPrice;

  useEffect(() => {
    if (!pickOpen) return;
    const onDoc = (e) => {
      if (icebergPanelRef.current && !icebergPanelRef.current.contains(e.target)) {
        setPickOpen(null);
      }
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [pickOpen]);

  const perSubOptions = useMemo(() => {
    const fmtLocal = typeof formatPriceThousands === "function" ? formatPriceThousands : (x) => x;
    const raw = String(value.totalQty || "").replace(/,/g, "");
    const t = parseFloat(raw);
    if (Number.isFinite(t) && t > 0) {
      const q10 = parseFloat((t * 0.1).toFixed(8));
      const q25 = parseFloat((t * 0.25).toFixed(8));
      const q50 = parseFloat((t * 0.5).toFixed(8));
      const q100 = parseFloat(t.toFixed(8));
      return [
        { label: `10% of total (${fmtLocal(String(q10))} ${b})`, value: String(q10) },
        { label: `25% of total (${fmtLocal(String(q25))} ${b})`, value: String(q25) },
        { label: `50% of total (${fmtLocal(String(q50))} ${b})`, value: String(q50) },
        { label: `100% of total (${fmtLocal(String(q100))} ${b})`, value: String(q100) },
      ];
    }
    return [
      { label: `0.0001 ${b}`, value: "0.0001" },
      { label: `0.0005 ${b}`, value: "0.0005" },
      { label: `0.001 ${b}`, value: "0.001" },
      { label: `0.01 ${b}`, value: "0.01" },
    ];
  }, [value.totalQty, b, formatPriceThousands]);

  const estFilled =
    value.perSubOrderQty && String(value.perSubOrderQty).trim() !== ""
      ? `${fmt(String(value.perSubOrderQty).replace(/,/g, ""))} ${b}`
      : `-- ${b}`;

  const pvPlaceholder = value.priceVarianceUnit === "%" ? "0.01 - 0.2%" : "USDT";

  return (
    <div ref={icebergPanelRef} className="margin-advanced-block margin-advanced-block--iceberg" data-margin-form="ICEBERG_PRO">
      <h6 className="agce-margin-iceberg-section-title">Price Setting</h6>

      <div className="form-group mb-3 trade_price_field_wrap">
        <label style={twapDotted}>Price Var.</label>
        <div
          className={`trade_price_field agce-margin-twap-interval-row${pickOpen === "pvUnit" ? " is-open" : ""
            }`}
        >
          <input
            type="text"
            className="trade_price_input"
            inputMode="decimal"
            autoComplete="off"
            placeholder={pvPlaceholder}
            value={value.priceVariance}
            onChange={(e) =>
              set((p) => ({ ...p, priceVariance: e.target.value.replace(/,/g, "") }))
            }
            aria-label="Iceberg price variance"
          />
          <div className="agce-margin-twap-interval-unit-wrap">
            <button
              type="button"
              className="agce-margin-twap-interval-unit-btn"
              onClick={() => setPickOpen((k) => (k === "pvUnit" ? null : "pvUnit"))}
              aria-expanded={pickOpen === "pvUnit"}
              aria-haspopup="menu"
            >
              <span>{value.priceVarianceUnit}</span>
              <i
                className={`agce-margin-twap-pick__chev ri-arrow-${pickOpen === "pvUnit" ? "up" : "down"}-s-line`}
                aria-hidden
              />
            </button>
            {pickOpen === "pvUnit" ? (
              <TwapPickMenu
                options={ICEBERG_PV_UNIT_OPTIONS}
                onPick={(v) => {
                  set((p) => ({ ...p, priceVarianceUnit: v }));
                  setPickOpen(null);
                }}
              />
            ) : null}
          </div>
        </div>
      </div>

      <div className="form-group mb-3 trade_price_field_wrap">
        <label style={twapDotted}>Activation Price</label>
        <div className="trade_price_field">
          <input
            type="text"
            className="trade_price_input"
            inputMode="decimal"
            autoComplete="off"
            placeholder="≤ Market Price"
            value={actDisplay}
            onFocus={() => setActFocus(true)}
            onBlur={() => setActFocus(false)}
            onChange={(e) =>
              set((p) => ({ ...p, activationPrice: e.target.value.replace(/,/g, "") }))
            }
            aria-label="Iceberg activation price"
          />
          <span className="trade_price_suffix">{q}</span>
        </div>
      </div>

      <h6 className="agce-margin-iceberg-section-title mt-4">Amount Setting</h6>

      <div className="form-group mb-2 trade_amount_field_wrap">
        <label>Total</label>
        <div className="input-group trade_amount_field_limit">
          <input
            type="text"
            className="form-control"
            inputMode="decimal"
            autoComplete="off"
            placeholder=""
            value={value.totalQty}
            onChange={(e) => set((p) => ({ ...p, totalQty: e.target.value.replace(/,/g, "") }))}
            aria-label="Iceberg total amount"
          />
          <span className="input-group-text text-start trade_amount_coin_badge">
            <small>{b}</small>
          </span>
        </div>
      </div>

      <div className="form-group mb-3 trade_amount_field_wrap">
        <label style={twapDotted}>Per Sub-Order</label>
        <div className={`agce-margin-twap-per-pick${pickOpen === "per" ? " is-open" : ""}`}>
          <div className="input-group trade_amount_field_limit">
            <button
              type="button"
              className={`form-control text-start border-0 shadow-none bg-transparent agce-margin-twap-pick-rowbtn agce-margin-twap-pick-rowbtn--per${!value.perSubOrderQty ? " agce-margin-twap-pick-rowbtn--muted" : ""
                }`}
              onClick={() => setPickOpen((k) => (k === "per" ? null : "per"))}
              aria-expanded={pickOpen === "per"}
              aria-haspopup="menu"
            >
              <span className="agce-margin-twap-pick-rowbtn__text">
                {value.perSubOrderQty
                  ? fmt(String(value.perSubOrderQty).replace(/,/g, ""))
                  : "< Total"}
              </span>
              <i
                className={`agce-margin-twap-pick__chev ri-arrow-${pickOpen === "per" ? "up" : "down"}-s-line`}
                aria-hidden
              />
            </button>
            <span className="input-group-text text-start trade_amount_coin_badge">
              <small>{b}</small>
            </span>
          </div>
          {pickOpen === "per" ? (
            <TwapPickMenu
              options={perSubOptions}
              onPick={(v) => {
                set((p) => ({ ...p, perSubOrderQty: v }));
                setPickOpen(null);
              }}
            />
          ) : null}
        </div>
      </div>

      <div className="agce-margin-twap-est-row mb-3">
        <span className="agce-margin-twap-est-label">Est. Filled Per Sub-Order:</span>
        <span className="agce-margin-twap-est-val">{estFilled}</span>
      </div>

      <div className="stop_avail_block limit_buy_avail mt-2">
        <div className="stop_avail_row limit_buy_avail_row">
          <span className="stop_avail_label">Available</span>
          <div className="stop_avail_rgt">
            <span className="stop_avail_val">{availLine}</span>
            <button type="button" className="limit_buy_plus" onClick={onPlusClick} aria-label="Add Funds">
              <img src="/images/plushicon.svg" alt="plus" />
            </button>
          </div>
        </div>
        {marginSurfaceTab !== "spot" && (
          <>
            <div className="stop_avail_row limit_buy_avail_row">
              <span className="stop_avail_label" style={twapDotted}>
                Max
              </span>
              <span className="stop_avail_val">{maxLine}</span>
            </div>
            <div className="stop_avail_row limit_buy_avail_row">
              <span className="stop_avail_label" style={twapDotted}>
                Borrowing
              </span>
              <span className="stop_avail_val">0 {q}</span>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function loopPriceModeLabel(mode) {
  const hit = LOOP_PRICE_MODE_OPTIONS.find((o) => o.value === mode);
  return hit?.label ?? "Last Price";
}

/** Loop Order — Limit tab–style filled form (`marginFormLoopOrder` in margin/index.js). */
function MarginLoopOrderFields({
  marginSurfaceTab,
  value,
  set,
  baseSymbol,
  quoteSymbol,
  side,
  token,
  buyCoinBal,
  sellCoinBal,
  formatTotal,
  onPlusClick,
}) {
  const q = quoteSymbol || "USDT";
  const b = baseSymbol || "BTC";
  const isBuy = side === "buy";
  const quoteBal = buyCoinBal != null ? parseFloat(Number(buyCoinBal).toFixed(8)) : 0;
  const baseBal = sellCoinBal != null ? parseFloat(Number(sellCoinBal).toFixed(8)) : 0;
  const availLine = isBuy ? `${token ? quoteBal : "0"} ${q}` : `${token ? baseBal : "0"} ${b}`;
  const maxLine = isBuy
    ? token && buyCoinBal != null
      ? `${formatTotal?.(buyCoinBal) ?? "0"} ${q}`
      : `0 ${q}`
    : token && sellCoinBal != null
      ? `${formatTotal?.(sellCoinBal) ?? "0"} ${b}`
      : `0 ${b}`;

  const [pickOpen, setPickOpen] = useState(null);
  const loopPanelRef = useRef(null);

  useEffect(() => {
    if (!pickOpen) return;
    const onDoc = (e) => {
      if (loopPanelRef.current && !loopPanelRef.current.contains(e.target)) {
        setPickOpen(null);
      }
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [pickOpen]);

  const priceInputVal = value.priceMode === "CUSTOM" ? value.price : "";

  return (
    <div ref={loopPanelRef} className="margin-advanced-block margin-advanced-block--loop" data-margin-form="LOOP_ORDER">
      <div className="form-group mb-3 trade_price_field_wrap">
        <label>Price</label>
        <div className={`trade_price_field agce-margin-loop-price-row${pickOpen === "pm" ? " is-open" : ""}`}>
          <input
            type="text"
            className="trade_price_input"
            inputMode="decimal"
            autoComplete="off"
            placeholder="Optional"
            readOnly={value.priceMode !== "CUSTOM"}
            value={priceInputVal}
            onChange={(e) => set((p) => ({ ...p, price: e.target.value.replace(/,/g, "") }))}
            aria-label="Loop order price"
          />
          <span className="agce-margin-loop-price-quote">{q}</span>
          <span className="agce-margin-loop-price-bar" aria-hidden>
            |
          </span>
          <div className="agce-margin-loop-price-mode">
            <button
              type="button"
              className="agce-margin-loop-price-mode-btn"
              onClick={() => setPickOpen((k) => (k === "pm" ? null : "pm"))}
              aria-expanded={pickOpen === "pm"}
              aria-haspopup="menu"
            >
              <span>{loopPriceModeLabel(value.priceMode)}</span>
              <i
                className={`agce-margin-loop-price-mode-chev ri-arrow-${pickOpen === "pm" ? "up" : "down"}-s-line`}
                aria-hidden
              />
            </button>
            {pickOpen === "pm" ? (
              <LoopPickMenu
                options={LOOP_PRICE_MODE_OPTIONS}
                selectedValue={value.priceMode}
                onPick={(v) => {
                  set((p) => ({
                    ...p,
                    priceMode: v,
                    price: v === "CUSTOM" ? p.price : "",
                  }));
                  setPickOpen(null);
                }}
              />
            ) : null}
          </div>
        </div>
      </div>

      <div className="form-group mb-3 trade_price_field_wrap">
        <label>Take Profit Percentage</label>
        <div className="trade_price_field">
          <input
            type="text"
            className="trade_price_input"
            inputMode="decimal"
            autoComplete="off"
            placeholder=""
            value={value.takeProfitPct}
            onChange={(e) =>
              set((p) => ({ ...p, takeProfitPct: e.target.value.replace(/,/g, "") }))
            }
            aria-label="Take profit percentage"
          />
          <span className="trade_price_suffix">%</span>
        </div>
      </div>

      <div className="form-group mb-3 trade_price_field_wrap">
        <label>Stop Loss Percentage</label>
        <div className="trade_price_field">
          <input
            type="text"
            className="trade_price_input"
            inputMode="decimal"
            autoComplete="off"
            placeholder=""
            value={value.stopLossPct}
            onChange={(e) =>
              set((p) => ({ ...p, stopLossPct: e.target.value.replace(/,/g, "") }))
            }
            aria-label="Stop loss percentage"
          />
          <span className="trade_price_suffix">%</span>
        </div>
      </div>

      <div className="form-group mb-3 trade_amount_field_wrap">
        <label>Pause Time After SL</label>
        <div className="input-group trade_amount_field_limit">
          <input
            type="text"
            className="form-control"
            inputMode="numeric"
            autoComplete="off"
            placeholder=""
            value={value.pauseAfterSlMin}
            onChange={(e) =>
              set((p) => ({ ...p, pauseAfterSlMin: e.target.value.replace(/[^\d.]/g, "") }))
            }
            aria-label="Pause time after stop loss in minutes"
          />
          <span className="input-group-text text-start trade_amount_coin_badge">
            <small>Min</small>
          </span>
        </div>
      </div>

      <div className="form-group mb-3 trade_amount_field_wrap">
        <label>Amount</label>
        <div className="input-group trade_amount_field_limit">
          <input
            type="text"
            className="form-control"
            inputMode="decimal"
            autoComplete="off"
            placeholder=""
            value={value.amount}
            onChange={(e) => set((p) => ({ ...p, amount: e.target.value.replace(/,/g, "") }))}
            aria-label="Amount"
          />
          <span className="input-group-text text-start trade_amount_coin_badge">
            <small>{b}</small>
          </span>
        </div>
      </div>

      <div className="form-group mb-3 trade_price_field_wrap">
        <label style={twapDotted}>Total Loops</label>
        <div className="trade_price_field">
          <input
            type="text"
            className="trade_price_input"
            inputMode="numeric"
            autoComplete="off"
            placeholder=""
            value={value.totalLoops}
            onChange={(e) =>
              set((p) => ({ ...p, totalLoops: e.target.value.replace(/[^\d.]/g, "") }))
            }
            aria-label="Total loops"
          />
        </div>
      </div>

      <div className="form-group mb-3 trade_price_field_wrap">
        <label>Valid for</label>
        <div className={`trade_price_field agce-margin-loop-valid-wrap${pickOpen === "vf" ? " is-open" : ""}`}>
          <button
            type="button"
            className="agce-margin-loop-valid-btn"
            onClick={() => setPickOpen((k) => (k === "vf" ? null : "vf"))}
            aria-expanded={pickOpen === "vf"}
            aria-haspopup="menu"
          >
            <span className="agce-margin-loop-valid-btn__text">{value.validFor}</span>
            <i
              className={`agce-margin-loop-valid-chev ri-arrow-${pickOpen === "vf" ? "up" : "down"}-s-line`}
              aria-hidden
            />
          </button>
          {pickOpen === "vf" ? (
            <LoopPickMenu
              options={LOOP_VALID_FOR_OPTIONS}
              selectedValue={value.validFor}
              onPick={(v) => {
                set((p) => ({ ...p, validFor: v }));
                setPickOpen(null);
              }}
            />
          ) : null}
        </div>
      </div>

      <div className="stop_avail_block limit_buy_avail mt-2">
        <div className="stop_avail_row limit_buy_avail_row">
          <span className="stop_avail_label">Available</span>
          <div className="stop_avail_rgt">
            <span className="stop_avail_val">{availLine}</span>
            <button type="button" className="limit_buy_plus" onClick={onPlusClick} aria-label="Add Funds">
              <img src="/images/plushicon.svg" alt="plus" />
            </button>
          </div>
        </div>
        {marginSurfaceTab !== "spot" && (
          <>
            <div className="stop_avail_row limit_buy_avail_row">
              <span className="stop_avail_label" style={twapDotted}>
                Max
              </span>
              <span className="stop_avail_val">{maxLine}</span>
            </div>
            <div className="stop_avail_row limit_buy_avail_row">
              <span className="stop_avail_label" style={twapDotted}>
                Borrowing
              </span>
              <span className="stop_avail_val">0 {q}</span>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

/** Trailing Stop — filled Limit-style form (`marginFormTrailingStop` in margin/index.js). */
function MarginTrailingStopFields({
  marginSurfaceTab,
  value,
  set,
  baseSymbol,
  quoteSymbol,
  side,
  token,
  buyCoinBal,
  sellCoinBal,
  formatTotal,
  formatPriceThousands,
  onPlusClick,
}) {
  const q = quoteSymbol || "USDT";
  const b = baseSymbol || "BTC";
  const isBuy = side === "buy";
  const quoteBal = buyCoinBal != null ? parseFloat(Number(buyCoinBal).toFixed(8)) : 0;
  const baseBal = sellCoinBal != null ? parseFloat(Number(sellCoinBal).toFixed(8)) : 0;
  const availLine = isBuy ? `${token ? quoteBal : "0"} ${q}` : `${token ? baseBal : "0"} ${b}`;
  const maxLine = isBuy
    ? token && buyCoinBal != null
      ? `${formatTotal?.(buyCoinBal) ?? "0"} ${q}`
      : `0 ${q}`
    : token && sellCoinBal != null
      ? `${formatTotal?.(sellCoinBal) ?? "0"} ${b}`
      : `0 ${b}`;

  const fmt = typeof formatPriceThousands === "function" ? formatPriceThousands : (x) => x;
  const [actFocus, setActFocus] = useState(false);
  const [pickOpen, setPickOpen] = useState(null);
  const trailPanelRef = useRef(null);

  const pullbackUnit = value.pullbackUnit === "QUOTE" ? "QUOTE" : "%";
  const pullbackUnitOptions = useMemo(
    () => [
      { label: "%", value: "%" },
      { label: q, value: "QUOTE" },
    ],
    [q],
  );

  useEffect(() => {
    if (!pickOpen) return;
    const onDoc = (e) => {
      if (trailPanelRef.current && !trailPanelRef.current.contains(e.target)) {
        setPickOpen(null);
      }
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [pickOpen]);

  const actDisplay =
    !actFocus && value.activationPrice && String(value.activationPrice).trim() !== ""
      ? fmt(String(value.activationPrice).replace(/,/g, ""))
      : value.activationPrice;

  const setPct = (pct) => {
    const safe = [0, 25, 50, 75, 100].includes(pct) ? pct : 0;
    set((p) => ({ ...p, amountPercent: safe }));
  };

  return (
    <div ref={trailPanelRef} className="margin-advanced-block margin-advanced-block--trail" data-margin-form="TRAILING_STOP">
      <div className="form-group mb-3 trade_price_field_wrap">
        <label>Pullback Rate</label>
        <div className="agce-margin-trail-pullback-row">
          <div
            className={`trade_price_field agce-margin-twap-interval-row agce-margin-trail-pullback-field${pickOpen === "pu" ? " is-open" : ""
              }`}
          >
            <input
              type="text"
              className="trade_price_input"
              inputMode="decimal"
              autoComplete="off"
              placeholder={pullbackUnit === "%" ? "0-100%" : `0 — ${q}`}
              value={value.pullbackRatePct}
              onChange={(e) =>
                set((p) => ({ ...p, pullbackRatePct: e.target.value.replace(/,/g, "") }))
              }
              aria-label={pullbackUnit === "%" ? "Pullback rate in percent" : `Pullback distance in ${q}`}
            />
            <div className="agce-margin-twap-interval-unit-wrap agce-margin-trail-pull-unit">
              <button
                type="button"
                className="agce-margin-twap-interval-unit-btn"
                onClick={() => setPickOpen((k) => (k === "pu" ? null : "pu"))}
                aria-expanded={pickOpen === "pu"}
                aria-haspopup="menu"
              >
                <span>{pullbackUnit === "QUOTE" ? q : "%"}</span>
                <i
                  className={`agce-margin-twap-pick__chev ri-arrow-${pickOpen === "pu" ? "up" : "down"}-s-line`}
                  aria-hidden
                />
              </button>
              {pickOpen === "pu" ? (
                <LoopPickMenu
                  options={pullbackUnitOptions}
                  selectedValue={pullbackUnit}
                  onPick={(v) => {
                    set((p) => ({ ...p, pullbackUnit: v }));
                    setPickOpen(null);
                  }}
                />
              ) : null}
            </div>
          </div>
          <button
            type="button"
            className="agce-margin-trail-preset-chip"
            onClick={() =>
              set((p) => ({
                ...p,
                pullbackRatePct: "5",
                pullbackUnit: "%",
              }))
            }
          >
            5%
          </button>
        </div>
      </div>

      <div className="form-group mb-3 trade_price_field_wrap">
        <label style={twapDotted}>Activation Price</label>
        <div className="trade_price_field">
          <input
            type="text"
            className="trade_price_input"
            inputMode="decimal"
            autoComplete="off"
            placeholder="Optional"
            value={actDisplay}
            onFocus={() => setActFocus(true)}
            onBlur={() => setActFocus(false)}
            onChange={(e) =>
              set((p) => ({ ...p, activationPrice: e.target.value.replace(/,/g, "") }))
            }
            aria-label="Activation price"
          />
          <span className="trade_price_suffix">{q}</span>
        </div>
      </div>

      <div className="form-group mb-3 trade_amount_field_wrap">
        <label>Amount</label>
        <div className="input-group trade_amount_field_limit">
          <input
            type="text"
            className="form-control"
            inputMode="decimal"
            autoComplete="off"
            placeholder=""
            value={value.amount}
            onChange={(e) => set((p) => ({ ...p, amount: e.target.value.replace(/,/g, "") }))}
            aria-label="Amount"
          />
          <span className="input-group-text text-start trade_amount_coin_badge">
            <small>{b}</small>
          </span>
        </div>
      </div>

      <div className="limit_buy_extras mb-3">
        <div className="stop_slider_row limit_buy_slider_row trade_pct_slider_row">
          <div className="trade_pct_slider_shell" style={{ "--fill-pct": `${value.amountPercent}%` }}>
            <div className="trade_pct_track_line" aria-hidden />
            <input
              className="trade_pct_slider_input"
              type="range"
              min={0}
              max={100}
              step={25}
              value={value.amountPercent}
              onChange={(e) => setPct(Number(e.target.value))}
              aria-label="Amount percentage"
            />
            <div className="trade_pct_marks" aria-hidden>
              {[0, 1, 2, 3, 4].map((step) => (
                <span
                  key={step}
                  className={`trade_pct_dot ${step === 0 ? "trade_pct_dot--anchor" : ""} ${step * 25 <= value.amountPercent ? "trade_pct_dot--fill" : ""
                    }`}
                />
              ))}
            </div>
            <div className="trade_pct_labels" aria-hidden>
              <span>0%</span>
              <span>25%</span>
              <span>50%</span>
              <span>75%</span>
              <span>100%</span>
            </div>
          </div>
        </div>
      </div>

      <div className="form-group mb-3 trade_price_field_wrap">
        <label style={twapDotted}>Valid for</label>
        <div className={`trade_price_field agce-margin-loop-valid-wrap${pickOpen === "vf" ? " is-open" : ""}`}>
          <button
            type="button"
            className="agce-margin-loop-valid-btn"
            onClick={() => setPickOpen((k) => (k === "vf" ? null : "vf"))}
            aria-expanded={pickOpen === "vf"}
            aria-haspopup="menu"
          >
            <span className="agce-margin-loop-valid-btn__text">{value.validFor}</span>
            <i
              className={`agce-margin-loop-valid-chev ri-arrow-${pickOpen === "vf" ? "up" : "down"}-s-line`}
              aria-hidden
            />
          </button>
          {pickOpen === "vf" ? (
            <LoopPickMenu
              options={LOOP_VALID_FOR_OPTIONS}
              selectedValue={value.validFor}
              onPick={(v) => {
                set((p) => ({ ...p, validFor: v }));
                setPickOpen(null);
              }}
            />
          ) : null}
        </div>
      </div>

      <div className="stop_avail_block limit_buy_avail mt-2">
        <div className="stop_avail_row limit_buy_avail_row">
          <span className="stop_avail_label">Available</span>
          <div className="stop_avail_rgt">
            <span className="stop_avail_val">{availLine}</span>
            <button type="button" className="limit_buy_plus" onClick={onPlusClick} aria-label="Add Funds">
              <img src="/images/plushicon.svg" alt="plus" />
            </button>
          </div>
        </div>
        {marginSurfaceTab !== "spot" && (
          <>
            <div className="stop_avail_row limit_buy_avail_row">
              <span className="stop_avail_label" style={twapDotted}>
                Max
              </span>
              <span className="stop_avail_val">{maxLine}</span>
            </div>
            <div className="stop_avail_row limit_buy_avail_row">
              <span className="stop_avail_label" style={twapDotted}>
                Borrowing
              </span>
              <span className="stop_avail_val">0 {q}</span>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

/** Generic Conditional — filled Limit-style (`marginFormConditional` in margin/index.js). */
function MarginConditionalFields({
  marginSurfaceTab,
  value,
  set,
  baseSymbol,
  quoteSymbol,
  side,
  token,
  buyCoinBal,
  sellCoinBal,
  formatTotal,
  formatPriceThousands,
  marketPriceSeed,
  onPlusClick,
}) {
  const q = quoteSymbol || "USDT";
  const b = baseSymbol || "BTC";
  const seedStr = normalizePriceSeed(marketPriceSeed);
  const fmt = typeof formatPriceThousands === "function" ? formatPriceThousands : (x) => x;
  const [trigFocus, setTrigFocus] = useState(false);
  const [pickOpen, setPickOpen] = useState(null);
  const condPanelRef = useRef(null);

  const isBuy = side === "buy";
  const quoteBal = buyCoinBal != null ? parseFloat(Number(buyCoinBal).toFixed(8)) : 0;
  const baseBal = sellCoinBal != null ? parseFloat(Number(sellCoinBal).toFixed(8)) : 0;
  const availLine = isBuy ? `${token ? quoteBal : "0"} ${q}` : `${token ? baseBal : "0"} ${b}`;
  const maxLine = isBuy
    ? token && buyCoinBal != null
      ? `${formatTotal?.(buyCoinBal) ?? "0"} ${q}`
      : `0 ${q}`
    : token && sellCoinBal != null
      ? `${formatTotal?.(sellCoinBal) ?? "0"} ${b}`
      : `0 ${b}`;

  const trigDisplay =
    !trigFocus && value.triggerPrice && String(value.triggerPrice).trim() !== ""
      ? fmt(String(value.triggerPrice).replace(/,/g, ""))
      : value.triggerPrice;

  useEffect(() => {
    if (!pickOpen) return;
    const onDoc = (e) => {
      if (condPanelRef.current && !condPanelRef.current.contains(e.target)) {
        setPickOpen(null);
      }
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [pickOpen]);

  const setPct = (pct) => {
    const safe = [0, 25, 50, 75, 100].includes(pct) ? pct : 0;
    set((p) => ({ ...p, amountPercent: safe }));
  };

  return (
    <div ref={condPanelRef} className="margin-advanced-block margin-advanced-block--cond" data-margin-form="CONDITIONAL">
      <div className="form-group mb-3 trade_price_field_wrap">
        <label>Trigger Price</label>
        <div className="trade_price_field">
          <input
            type="text"
            className="trade_price_input"
            inputMode="decimal"
            autoComplete="off"
            placeholder=""
            value={trigDisplay}
            onFocus={() => setTrigFocus(true)}
            onBlur={() => setTrigFocus(false)}
            onChange={(e) => set((p) => ({ ...p, triggerPrice: e.target.value.replace(/,/g, "") }))}
            aria-label="Trigger price"
          />
          <span className="trade_price_suffix">{q}</span>
        </div>
      </div>

      <TpSlLimitMarketRow
        label="Order Price"
        isMarket={value.orderMarket}
        onToggle={() =>
          set((p) =>
            p.orderMarket
              ? { ...p, orderMarket: false, orderLimitPrice: p.orderLimitPrice || seedStr }
              : { ...p, orderMarket: true, orderLimitPrice: "" },
          )
        }
        limitPrice={value.orderLimitPrice}
        onLimitPriceChange={(v) => set((p) => ({ ...p, orderLimitPrice: v }))}
        quoteSymbol={q}
        formatPriceThousands={formatPriceThousands}
      />

      <div className="form-group mb-3 trade_amount_field_wrap">
        <label>Amount</label>
        <div className="input-group trade_amount_field_limit">
          <input
            type="text"
            className="form-control"
            inputMode="decimal"
            autoComplete="off"
            placeholder=""
            value={value.amount}
            onChange={(e) => set((p) => ({ ...p, amount: e.target.value.replace(/,/g, "") }))}
            aria-label="Amount"
          />
          <span className="input-group-text text-start trade_amount_coin_badge">
            <small>{b}</small>
          </span>
        </div>
      </div>

      <div className="limit_buy_extras mb-3">
        <div className="stop_slider_row limit_buy_slider_row trade_pct_slider_row">
          <div className="trade_pct_slider_shell" style={{ "--fill-pct": `${value.amountPercent ?? 0}%` }}>
            <div className="trade_pct_track_line" aria-hidden />
            <input
              className="trade_pct_slider_input"
              type="range"
              min={0}
              max={100}
              step={25}
              value={value.amountPercent ?? 0}
              onChange={(e) => setPct(Number(e.target.value))}
              aria-label="Amount percentage"
            />
            <div className="trade_pct_marks" aria-hidden>
              {[0, 1, 2, 3, 4].map((step) => (
                <span
                  key={step}
                  className={`trade_pct_dot ${step === 0 ? "trade_pct_dot--anchor" : ""} ${step * 25 <= (value.amountPercent ?? 0) ? "trade_pct_dot--fill" : ""
                    }`}
                />
              ))}
            </div>
            <div className="trade_pct_labels" aria-hidden>
              <span>0%</span>
              <span>25%</span>
              <span>50%</span>
              <span>75%</span>
              <span>100%</span>
            </div>
          </div>
        </div>
      </div>

      <div className="form-group mb-3 trade_price_field_wrap">
        <label style={twapDotted}>Valid for</label>
        <div className={`trade_price_field agce-margin-loop-valid-wrap${pickOpen === "vf" ? " is-open" : ""}`}>
          <button
            type="button"
            className="agce-margin-loop-valid-btn"
            onClick={() => setPickOpen((k) => (k === "vf" ? null : "vf"))}
            aria-expanded={pickOpen === "vf"}
            aria-haspopup="menu"
          >
            <span className="agce-margin-loop-valid-btn__text">{value.validFor}</span>
            <i
              className={`agce-margin-loop-valid-chev ri-arrow-${pickOpen === "vf" ? "up" : "down"}-s-line`}
              aria-hidden
            />
          </button>
          {pickOpen === "vf" ? (
            <LoopPickMenu
              options={CONDITIONAL_VALID_FOR_OPTIONS}
              selectedValue={value.validFor}
              onPick={(v) => {
                set((p) => ({ ...p, validFor: v }));
                setPickOpen(null);
              }}
            />
          ) : null}
        </div>
      </div>

      <div className="stop_avail_block limit_buy_avail mt-2">
        <div className="stop_avail_row limit_buy_avail_row">
          <span className="stop_avail_label">Available</span>
          <div className="stop_avail_rgt">
            <span className="stop_avail_val">{availLine}</span>
            <button type="button" className="limit_buy_plus" onClick={onPlusClick} aria-label="Add Funds">
              <img src="/images/plushicon.svg" alt="plus" />
            </button>
          </div>
        </div>
        {marginSurfaceTab !== "spot" && (
          <>
            <div className="stop_avail_row limit_buy_avail_row">
              <span className="stop_avail_label" style={twapDotted}>
                Max
              </span>
              <span className="stop_avail_val">{maxLine}</span>
            </div>
            <div className="stop_avail_row limit_buy_avail_row">
              <span className="stop_avail_label" style={twapDotted}>
                Borrowing
              </span>
              <span className="stop_avail_val">0 {q}</span>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

/** Advanced Limit — filled Limit tab shell + Order Type modal (`marginFormAdvancedLimit` in margin/index.js). */
function MarginAdvancedLimitFields({
  marginSurfaceTab,
  value,
  set,
  baseSymbol,
  quoteSymbol,
  side,
  token,
  buyCoinBal,
  sellCoinBal,
  formatTotal,
  formatPriceThousands,
  advLimitPriceMeta,
  onPlusClick,
}) {
  const q = quoteSymbol || "USDT";
  const b = baseSymbol || "BTC";
  const tick = advLimitPriceMeta?.tick ?? 0.01;
  const prec = advLimitPriceMeta?.prec ?? 8;
  const fmt = typeof formatPriceThousands === "function" ? formatPriceThousands : (x) => x;
  const [otModalOpen, setOtModalOpen] = useState(false);
  const [priceFocus, setPriceFocus] = useState(false);
  const isBuy = side === "buy";
  const quoteBal = buyCoinBal != null ? parseFloat(Number(buyCoinBal).toFixed(8)) : 0;
  const baseBal = sellCoinBal != null ? parseFloat(Number(sellCoinBal).toFixed(8)) : 0;
  const availLine = isBuy ? `${token ? quoteBal : "0"} ${q}` : `${token ? baseBal : "0"} ${b}`;
  const maxLine = isBuy
    ? token && buyCoinBal != null
      ? `${formatTotal?.(buyCoinBal) ?? "0"} ${q}`
      : `0 ${q}`
    : token && sellCoinBal != null
      ? `${formatTotal?.(sellCoinBal) ?? "0"} ${b}`
      : `0 ${b}`;

  const orderTypeKey = ["POST_ONLY", "FOK", "IOC"].includes(value.orderType) ? value.orderType : "POST_ONLY";

  const limitDisplay =
    !priceFocus && value.limitPrice && String(value.limitPrice).trim() !== ""
      ? fmt(String(value.limitPrice).replace(/,/g, ""))
      : value.limitPrice;

  const setPct = (pct) => {
    const safe = [0, 25, 50, 75, 100].includes(pct) ? pct : 0;
    set((p) => ({ ...p, amountPercent: safe }));
  };

  return (
    <>
      <div className="margin-advanced-block margin-advanced-block--adv-lim" data-margin-form="ADVANCED_LIMIT">
        <div className="form-group mb-3 trade_price_field_wrap">
          <label>Order Type</label>
          <div className="trade_price_field agce-margin-loop-valid-wrap" style={{ padding: 0 }}>
            <button
              type="button"
              className="agce-margin-loop-valid-btn"
              onClick={() => setOtModalOpen(true)}
              aria-haspopup="dialog"
              aria-expanded={otModalOpen}
            >
              <span className="agce-margin-loop-valid-btn__text">{advancedLimitOrderTypeLabel(orderTypeKey)}</span>
              <i className="agce-margin-loop-valid-chev ri-arrow-down-s-line" aria-hidden />
            </button>
          </div>
        </div>

        <div className="form-group mb-3 trade_price_field_wrap">
          <label>Price</label>
          <div className="trade_price_field">
            <input
              type="text"
              className="trade_price_input"
              inputMode="decimal"
              autoComplete="off"
              placeholder=""
              value={limitDisplay}
              onFocus={() => setPriceFocus(true)}
              onBlur={() => setPriceFocus(false)}
              onChange={(e) => set((p) => ({ ...p, limitPrice: e.target.value.replace(/,/g, "") }))}
              aria-label="Limit price"
            />
            <span className="trade_price_suffix">{q}</span>
            <div className="trade_price_stepper" role="group" aria-label="Adjust price">
              <button
                type="button"
                className="trade_price_step_btn"
                aria-label="Increase price"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() =>
                  set((p) => ({
                    ...p,
                    limitPrice: nudgeAdvLimitPriceStr(p.limitPrice, 1, tick, prec),
                  }))
                }
              >
                <span className="trade_price_step_icon trade_price_step_up" aria-hidden />
              </button>
              <button
                type="button"
                className="trade_price_step_btn lastbtnprice"
                aria-label="Decrease price"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() =>
                  set((p) => ({
                    ...p,
                    limitPrice: nudgeAdvLimitPriceStr(p.limitPrice, -1, tick, prec),
                  }))
                }
              >
                <span className="trade_price_step_icon trade_price_step_down" aria-hidden />
              </button>
            </div>
          </div>
        </div>

        <div className="form-group mb-3 trade_amount_field_wrap">
          <label>Amount</label>
          <div className="input-group trade_amount_field_limit">
            <input
              type="text"
              className="form-control"
              inputMode="decimal"
              autoComplete="off"
              placeholder=""
              value={value.amount}
              onChange={(e) => set((p) => ({ ...p, amount: e.target.value.replace(/,/g, "") }))}
              aria-label="Amount"
            />
            <span className="input-group-text text-start trade_amount_coin_badge">
              <small>{b}</small>
            </span>
          </div>
        </div>

        <div className="limit_buy_extras mb-3">
          <div className="stop_slider_row limit_buy_slider_row trade_pct_slider_row">
            <div className="trade_pct_slider_shell" style={{ "--fill-pct": `${value.amountPercent}%` }}>
              <div className="trade_pct_track_line" aria-hidden />
              <input
                className="trade_pct_slider_input"
                type="range"
                min={0}
                max={100}
                step={25}
                value={value.amountPercent}
                onChange={(e) => setPct(Number(e.target.value))}
                aria-label="Amount percentage"
              />
              <div className="trade_pct_marks" aria-hidden>
                {[0, 1, 2, 3, 4].map((step) => (
                  <span
                    key={step}
                    className={`trade_pct_dot ${step === 0 ? "trade_pct_dot--anchor" : ""} ${step * 25 <= value.amountPercent ? "trade_pct_dot--fill" : ""
                      }`}
                  />
                ))}
              </div>
              <div className="trade_pct_labels" aria-hidden>
                <span>0%</span>
                <span>25%</span>
                <span>50%</span>
                <span>75%</span>
                <span>100%</span>
              </div>
            </div>
          </div>
        </div>

        <div className="form-group mb-3 trade_price_field_wrap">
          <label>Total</label>
          <div className="trade_price_field is-readonly">
            <input
              type="text"
              className="trade_price_input agce-margin-advlim-total-input"
              readOnly
              tabIndex={-1}
              placeholder="≥ 1"
              value={value.total}
              aria-label="Total"
            />
            <span className="trade_price_suffix">{q}</span>
          </div>
        </div>


        <div className="stop_avail_block limit_buy_avail mt-2">
          <div className="stop_avail_row limit_buy_avail_row">
            <span className="stop_avail_label">Available</span>
            <div className="stop_avail_rgt">
              <span className="stop_avail_val">{availLine}</span>
              <button type="button" className="limit_buy_plus" onClick={onPlusClick} aria-label="Add Funds">
                <img src="/images/plushicon.svg" alt="plus" />
              </button>
            </div>
          </div>
          {marginSurfaceTab !== "spot" && (
            <>
              <div className="stop_avail_row limit_buy_avail_row">
                <span className="stop_avail_label">Max</span>
                <span className="stop_avail_val">{maxLine}</span>
              </div>
              <div className="stop_avail_row limit_buy_avail_row">
                <span className="stop_avail_label" style={twapDotted}>
                  Borrowing
                </span>
                <span className="stop_avail_val">0 {q}</span>
              </div>
            </>
          )}
        </div>

        <div className="stop_checks limit_buy_checks d-flex flex-column gap-2 mt-2">
          <label className="d-flex align-items-center gap-2 mb-0" style={{ cursor: "pointer" }}>
            <input
              type="checkbox"
              checked={!!value.tpSl}
              onChange={(e) => set((p) => ({ ...p, tpSl: e.target.checked }))}
            />
            <span style={{ borderBottom: "1px dashed #999" }}>TP/SL</span>
          </label>
          {value.tpSl && (
            <div className="agce-margin-advlim-tp-sl-wrap mt-1">
              <div className="form-group mb-2">
                <label>TP</label>
                <div className="input-group trade_amount_field_limit">
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Trigger Price"
                    value={value.tpPrice}
                    onChange={(e) => set((p) => ({ ...p, tpPrice: e.target.value.replace(/,/g, "") }))}
                    aria-label="Take profit trigger price"
                  />
                  <span className="input-group-text text-start trade_amount_coin_badge">
                    <small>{q}</small>
                  </span>
                </div>
              </div>
              <div className="form-group mb-2">
                <label>SL</label>
                <div className="input-group trade_amount_field_limit">
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Trigger Price"
                    value={value.slPrice}
                    onChange={(e) => set((p) => ({ ...p, slPrice: e.target.value.replace(/,/g, "") }))}
                    aria-label="Stop loss trigger price"
                  />
                  <span className="input-group-text text-start trade_amount_coin_badge">
                    <small>{q}</small>
                  </span>
                </div>
              </div>
            </div>
          )}

          <label className="d-flex align-items-center gap-2 mb-0" style={{ cursor: "pointer" }}>
            <input
              type="checkbox"
              checked={!!value.iceberg}
              onChange={(e) => set((p) => ({ ...p, iceberg: e.target.checked }))}
            />
            <span style={{ borderBottom: "1px dashed #999" }}>Iceberg</span>
          </label>
          {value.iceberg && (
            <div className="form-group mb-2 mt-1">
              <div className="input-group trade_amount_field_limit">
                <input
                  type="text"
                  className="form-control"
                  placeholder="Qty"
                  value={value.icebergQty}
                  onChange={(e) => set((p) => ({ ...p, icebergQty: e.target.value.replace(/,/g, "") }))}
                  aria-label="Iceberg quantity"
                />
                <span className="input-group-text text-start trade_amount_coin_badge">
                  <small>{b}</small>
                </span>
              </div>
            </div>
          )}
        </div>
      </div>

      <MarginAdvancedLimitOrderTypeModal
        open={otModalOpen}
        onClose={() => setOtModalOpen(false)}
        orderType={orderTypeKey}
        onConfirm={(next) => set((p) => ({ ...p, orderType: next }))}
      />
    </>
  );
}

function MarginPovFields({
  marginSurfaceTab,
  value,
  set,
  baseSymbol,
  quoteSymbol,
  side,
  token,
  buyCoinBal,
  sellCoinBal,
  formatTotal,
  formatPriceThousands,
  onPlusClick,
}) {
  const q = quoteSymbol || "USDT";
  const b = baseSymbol || "BTC";
  const isBuy = side === "buy";
  const quoteBal = buyCoinBal != null ? parseFloat(Number(buyCoinBal).toFixed(8)) : 0;
  const baseBal = sellCoinBal != null ? parseFloat(Number(sellCoinBal).toFixed(8)) : 0;
  const availLine = isBuy ? `${token ? quoteBal : "0"} ${q}` : `${token ? baseBal : "0"} ${b}`;
  const maxLine = isBuy
    ? token && buyCoinBal != null
      ? `${formatTotal?.(buyCoinBal) ?? "0"} ${q}`
      : `0 ${q}`
    : token && sellCoinBal != null
      ? `${formatTotal?.(sellCoinBal) ?? "0"} ${b}`
      : `0 ${b}`;

  const rates = ["Low", "Medium", "High", "Very High"];
  const days = ["1 Days", "2 Days", "3 Days", "4 Days", "5 Days"];

  const setRate = (r) => set((p) => ({ ...p, targetParticipationRate: r }));
  const toggleAdv = () => set((p) => ({ ...p, advancedOptionsOpen: !p.advancedOptionsOpen }));

  return (
    <div className="margin-advanced-block margin-advanced-block--pov" data-margin-form="POV">
      <div className="form-group mb-2 trade_amount_field_wrap">
        <label>Amount</label>
        <div className="input-group trade_amount_field_limit">
          <input
            type="text"
            className="form-control"
            inputMode="decimal"
            autoComplete="off"
            placeholder=""
            value={value.amount}
            onChange={(e) => set((p) => ({ ...p, amount: e.target.value.replace(/,/g, "") }))}
            aria-label="Amount"
          />
          <span className="input-group-text text-start trade_amount_coin_badge">
            <small>{b}</small>
          </span>
        </div>
      </div>

      <div className="form-group mb-2 trade_amount_field_wrap">
        <label className="agce-margin-pov-dotted-label">Target Participation Rate</label>
        <div className="d-flex gap-2 agce-margin-pov-rates-container">
          {rates.map(r => (
            <button
              key={r}
              type="button"
              className={`btn btn-sm flex-fill agce-margin-pov-rate-btn ${value.targetParticipationRate === r ? 'btn-outline-dark bg-white text-dark border-dark' : 'btn-light text-muted'}`}
              onClick={() => setRate(r)}
            >
              {r}
            </button>
          ))}
        </div>
      </div>

      <div className="agce-margin-twap-est-row mb-3 mt-3">
        <span className="agce-margin-twap-est-label agce-margin-pov-dotted-label">Estimated Duration</span>
        <span className="agce-margin-twap-est-val"> : --</span>
      </div>

      <div className="form-group mb-3 trade_amount_field_wrap">
        <label className="agce-margin-pov-dotted-label">Valid for</label>
        <select
          className="form-select bg-light border-0 shadow-none agce-margin-pov-valid-select"
          value={value.validFor}
          onChange={(e) => set((p) => ({ ...p, validFor: e.target.value }))}
        >
          {days.map(d => <option key={d} value={d}>{d}</option>)}
        </select>
      </div>

      <button
        type="button"
        className="agce-margin-twap-advanced-toggle mb-3"
        onClick={toggleAdv}
        aria-expanded={value.advancedOptionsOpen}
      >
        <span className="agce-margin-twap-advanced-toggle__label text-dark fw-bold agce-margin-pov-adv-toggle-label">Advanced Options</span>
        <i className={`agce-margin-twap-advanced-toggle__icon text-dark fw-bold ri-arrow-${value.advancedOptionsOpen ? "up" : "down"}-s-fill`} aria-hidden />
      </button>

      {value.advancedOptionsOpen && (
        <div className="agce-margin-twap-advanced-panel mb-3">
          <div className="form-group mb-2 trade_amount_field_wrap">
            <label>Trigger Price</label>
            <div className="input-group trade_amount_field_limit bg-light agce-margin-pov-adv-input-group">
              <input
                type="text"
                className="form-control bg-transparent border-0"
                inputMode="decimal"
                autoComplete="off"
                placeholder=""
                value={value.triggerPrice}
                onChange={(e) => set((p) => ({ ...p, triggerPrice: e.target.value.replace(/,/g, "") }))}
              />
              <span className="input-group-text bg-transparent border-0 text-start trade_amount_coin_badge">
                <small className="text-dark">{q}</small>
              </span>
            </div>
          </div>
          <div className="form-group mb-2 trade_amount_field_wrap">
            <label>Price Limit</label>
            <div className="input-group trade_amount_field_limit bg-light agce-margin-pov-adv-input-group">
              <input
                type="text"
                className="form-control bg-transparent border-0"
                inputMode="decimal"
                autoComplete="off"
                placeholder=""
                value={value.priceLimit}
                onChange={(e) => set((p) => ({ ...p, priceLimit: e.target.value.replace(/,/g, "") }))}
              />
              <span className="input-group-text bg-transparent border-0 text-start trade_amount_coin_badge">
                <small className="text-dark">{q}</small>
              </span>
            </div>
          </div>
        </div>
      )}

      <div className="stop_avail_block limit_buy_avail mt-2">
        <div className="stop_avail_row limit_buy_avail_row">
          <span className="stop_avail_label">Available</span>
          <div className="stop_avail_rgt">
            <span className="stop_avail_val">{availLine}</span>
            <button type="button" className="limit_buy_plus" onClick={onPlusClick} aria-label="Add Funds">
              <img src="/images/plushicon.svg" alt="plus" />
            </button>
          </div>
        </div>
        {marginSurfaceTab !== "spot" && marginSurfaceTab !== "etf" && (
          <>
            <div className="stop_avail_row limit_buy_avail_row">
              <span className="stop_avail_label agce-margin-pov-dotted-label">
                Max
              </span>
              <span className="stop_avail_val">{maxLine}</span>
            </div>
            <div className="stop_avail_row limit_buy_avail_row">
              <span className="stop_avail_label agce-margin-pov-dotted-label">
                Borrowing
              </span>
              <span className="stop_avail_val">0 {q}</span>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default function MarginAdvancedFields({
  marginSurfaceTab,
  orderKind,
  baseSymbol,
  quoteSymbol,
  forms,
  side = "buy",
  token = false,
  buyCoinBal,
  sellCoinBal,
  formatTotal,
  formatPriceThousands,
  marketPriceSeed,
  advLimitPriceMeta = { tick: 0.01, prec: 8 },
  onPlusClick,
}) {
  const q = quoteSymbol || "USDT";
  const b = baseSymbol || "BTC";

  if (orderKind === "CONDITIONAL") {
    const cond = forms?.conditional;
    if (!cond?.set || cond.value == null) {
      return null;
    }
    const { value, set } = cond;
    return (
      <MarginConditionalFields
        marginSurfaceTab={marginSurfaceTab}
        value={value}
        set={set}
        baseSymbol={b}
        quoteSymbol={q}
        side={side}
        token={token}
        buyCoinBal={buyCoinBal}
        sellCoinBal={sellCoinBal}
        formatTotal={formatTotal}
        formatPriceThousands={formatPriceThousands}
        marketPriceSeed={marketPriceSeed}
        onPlusClick={onPlusClick}
      />
    );
  }

  if (orderKind === "TP_SL") {
    const { value, set } = forms.tpSl;
    return (
      <MarginTpSlFields
        marginSurfaceTab={marginSurfaceTab}
        value={value}
        set={set}
        baseSymbol={b}
        quoteSymbol={q}
        side={side}
        token={token}
        buyCoinBal={buyCoinBal}
        sellCoinBal={sellCoinBal}
        formatTotal={formatTotal}
        formatPriceThousands={formatPriceThousands}
        marketPriceSeed={marketPriceSeed}
        onPlusClick={onPlusClick}
      />
    );
  }

  if (orderKind === "TWAP") {
    const { value, set } = forms.twap;
    return (
      <MarginTwapFields
        marginSurfaceTab={marginSurfaceTab}
        value={value}
        set={set}
        baseSymbol={b}
        quoteSymbol={q}
        side={side}
        token={token}
        buyCoinBal={buyCoinBal}
        sellCoinBal={sellCoinBal}
        formatTotal={formatTotal}
        formatPriceThousands={formatPriceThousands}
        onPlusClick={onPlusClick}
      />
    );
  }

  if (orderKind === "ICEBERG_PRO") {
    const { value, set } = forms.icebergPro;
    return (
      <MarginIcebergProFields
        marginSurfaceTab={marginSurfaceTab}
        value={value}
        set={set}
        baseSymbol={b}
        quoteSymbol={q}
        side={side}
        token={token}
        buyCoinBal={buyCoinBal}
        sellCoinBal={sellCoinBal}
        formatTotal={formatTotal}
        formatPriceThousands={formatPriceThousands}
        onPlusClick={onPlusClick}
      />
    );
  }

  if (orderKind === "LOOP_ORDER") {
    const { value, set } = forms.loopOrder;
    return (
      <MarginLoopOrderFields
        marginSurfaceTab={marginSurfaceTab}
        value={value}
        set={set}
        baseSymbol={b}
        quoteSymbol={q}
        side={side}
        token={token}
        buyCoinBal={buyCoinBal}
        sellCoinBal={sellCoinBal}
        formatTotal={formatTotal}
        onPlusClick={onPlusClick}
      />
    );
  }

  if (orderKind === "TRAILING_STOP") {
    const { value, set } = forms.trailingStop;
    return (
      <MarginTrailingStopFields
        marginSurfaceTab={marginSurfaceTab}
        value={value}
        set={set}
        baseSymbol={b}
        quoteSymbol={q}
        side={side}
        token={token}
        buyCoinBal={buyCoinBal}
        sellCoinBal={sellCoinBal}
        formatTotal={formatTotal}
        formatPriceThousands={formatPriceThousands}
        onPlusClick={onPlusClick}
      />
    );
  }

  if (orderKind === "ADVANCED_LIMIT") {
    const { value, set } = forms.advancedLimit;
    return (
      <MarginAdvancedLimitFields
        marginSurfaceTab={marginSurfaceTab}
        value={value}
        set={set}
        baseSymbol={b}
        quoteSymbol={q}
        side={side}
        token={token}
        buyCoinBal={buyCoinBal}
        sellCoinBal={sellCoinBal}
        formatTotal={formatTotal}
        formatPriceThousands={formatPriceThousands}
        advLimitPriceMeta={advLimitPriceMeta}
        onPlusClick={onPlusClick}
      />
    );
  }

  if (orderKind === "POV") {
    const { value, set } = forms.pov;
    return (
      <MarginPovFields
        marginSurfaceTab={marginSurfaceTab}
        value={value}
        set={set}
        baseSymbol={b}
        quoteSymbol={q}
        side={side}
        token={token}
        buyCoinBal={buyCoinBal}
        sellCoinBal={sellCoinBal}
        formatTotal={formatTotal}
        formatPriceThousands={formatPriceThousands}
        onPlusClick={onPlusClick}
      />
    );
  }

  return null;
}
